import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { ceil, floor, max, min } from "mathjs";
import { Chart } from "react-google-charts";
import {
	BOX_SHADOWS,
	FONTS,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
} from "../../constants";
import { formatNumber } from "../../helpers/FormatNumber";
import { stateType } from "../../state";
import { ChartYTitle } from "../CohortAnalysis/Charts/ColumnChart";
import { BoxHeader } from "../Common/BoxHeader";

const N_BINS = 20;
const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dropDown: {
			width: "140px",
			height: "30px",
			border: "solid",
			marginLeft: "20px",
			borderRadius: "5px",
			borderColor: "rgba(0,0,0,0.2)",
			borderWidth: "2px",
			marginTop: "-5px",
			display: "flex",
			flexDirection: "row",
			transition: "0.3s",
			cursor: "pointer",
			backgroundColor: "white",
			"&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
		},
		container: {
			display: "flex",
			flexDirection: "column",
			width: "100%",
			backgroundColor: "white",
			boxShadow: BOX_SHADOWS,
			borderRadius: "10px",
			height: "620px",
		},
		chartContainer: {
			width: "94%",
			height: "70%",
			marginLeft: "5%",
		},
		chartContainer2: {
			width: "100%",
			height: "100%",
			display: "flex",
			flexDirection: "column",
			position: "relative",
		},
	})
);

function NumericOutputChart({ state }: stateType) {
	const classes = useStyles();

	let data: any[] = [
		[
			"",
			"",
			{ role: "style" },
			{ role: "tooltip" },
			{ id: "i0", role: "interval" },
			{ id: "i1", role: "interval" },
			{ id: "i2", role: "interval" },
			{ id: "i3", role: "interval" },
		],
	];

	const ndx = state.saveState.sentiment.varSelected;
	const baseCol = state.saveState.dataset.nlpColumns[ndx];
	const colName = "Sentiment: " + baseCol;
	const sentData = state.saveState.dataset.getColumn(colName).vectorizedData;

	let startPoints = Array(N_BINS + 1)
		.fill(0)
		.map((v, i) => -100 + (i * 200) / N_BINS);
	let counts = Array(N_BINS).fill(0);

	for (let i = 0; i < sentData.length; i++) {
		for (let j = 0; j < counts.length; j++) {
			if (
				sentData[i] >= startPoints[j] &&
				sentData[i] < startPoints[j + 1]
			) {
				counts[j] += 1;
				break;
			}
		}
	}
	let maxVal = max(sentData);
	let minVal = min(sentData);
	counts.push(0);
	// Smooth out the counts
	for (let i = 0; i < counts.length + 1; i++) {
		data.push([
			startPoints[i],
			counts[i],
			`fill-color: ${SECONDARY_COLOR}; fill-opacity: ${0.7}; stroke-color: ${PRIMARY_COLOR}  ; stroke-width: 3;`,
			startPoints[i] === 100
				? ""
				: formatNumber(counts[i]) +
				  ` \n${startPoints[i]}>=Sentiment<${startPoints[i + 1]}`,
			0,
			counts[i],
			0,
			0,
		]);
	}

	return (
		<div className={classes.chartContainer}>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					marginTop: "10px",
				}}
			>
				<ChartYTitle yName={"Count"}></ChartYTitle>
				<div className={classes.chartContainer2}>
					<ChartXTitle xName={"Sentiment Score"}></ChartXTitle>
					<div
						style={{
							height: "100%",
							width: "100%",
							marginLeft: "0%",
						}}
					>
						<Chart
							style={{
								height: "500px",
								width: "100%",
								zIndex: 1000,
							}}
							chartType={"LineChart"}
							loader={<div>Loading Chart</div>}
							data={data}
							options={{
								intervals: {
									style: "area",
									color: SECONDARY_COLOR,
									fillOpacity: 0.8,
								},

								curveType: "function",
								// Material design options
								pointSize: 0,
								colors: [PRIMARY_COLOR, PRIMARY_COLOR],
								legend: { position: "none" },
								chartArea: {
									width: "100%",
									height: "100%",
									top: 10,
									right: 40,
									bottom: 60,
									left: 70,
								},

								vAxis: {
									minorGridlines: { color: "white" },
									gridlines: { color: "rgb(220,220,220)" },
									baselineColor: "rgb(220,220,220)",
									format: "#",
									viewWindow: { min: 0 },
								},
								hAxis: {
									minorGridlines: { color: "white" },
									baselineColor: "transparent",
									gridlines: { color: "rgb(220,220,220)" },
									viewWindow: {
										min: Math.max(floor(minVal), -100),
										max: Math.min(ceil(maxVal), 100),
									},
								},

								annotations: {
									alwaysOutside: true,
									textStyle: {
										color: PRIMARY_COLOR,
										fontSize: 14,
										fontFamily: FONTS,
									},
								},
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export function ChartXTitle({ xName }) {
	return (
		<div
			style={{
				color: "rgba(0,0,0,0.6)",
				fontFamily: FONTS,
				fontSize: "14px",
				zIndex: 1000,
				marginTop: "500px",
				marginBottom: "-500px",
				width: "100%",
				marginLeft: "20px",
				height: "0px",
				textAlign: "center",
			}}
		>
			{xName}
		</div>
	);
}
export function SentimentBox({ state }: stateType) {
	const classes = useStyles();

	const ndx = state.saveState.sentiment.varSelected;
	const baseCol = state.saveState.dataset.nlpColumns[ndx];
	return (
		<div className={classes.container}>
			<BoxHeader
				title={`Sentiment Distribution of '${baseCol}'`}
				toolTip={`Shows which variables have the largest relative influence on predicting ${state.saveState.modelTraining.valueToPredict}.`}
				line={true}
			></BoxHeader>

			<NumericOutputChart state={state}></NumericOutputChart>
		</div>
	);
}
